import React, { useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { useDialogBoxContext } from "../../../../../../../../../components/decisionDialogBox/decisionDialogBoxContext";
import { useBrowserClosureWarning } from "../../../../../../../../../utils/functions";
import { decisionDialogBoxMessage, decisionDialogBoxTitle } from "../../../../../../../../../components/decisionDialogBox/decisionDialogBox";
import { STANDARD_HEIGHT_OF_DROPDOWN } from "../../../../../../../../../utils/constants";
import { thinDarkScrollbar } from "../../../../../../../../../utils/scrollbarStyles";

interface AddExceptionFormProps {
  milestones: string[];
  selectedMilestone: string;
  setSelectedMilestone: (milestone: string) => void;
  handleSaveException: (
    exceptionName: string,
    description: string,
    mileStoneName: string,
    selectedType: exceptionTypes
  ) => void;
  handleBack: () => void;
  exceptionName: string;
  setExceptionName: (name: string) => void;
  description: string;
  setDescription: (desc: string) => void;
  exceptionType: exceptionTypes | null;
  setExceptionType: (type: exceptionTypes | null) => void;
  onExceptionDirtyChange?: (dirty: boolean) => void;
}

export enum exceptionTypes {
  Construction = "Construction",
  Mortgage = "Mortgage",
  Appraisal = "Appraisal",
  BusinessDecision = "Business Decision",
  BuyerPerformance = "Buyer Performance",
  BuyerRequest = "Buyer Request",
  ConvertedAlert = "Converted Risk",
}

const AddExceptionForm: React.FC<AddExceptionFormProps> = ({
  milestones,
  selectedMilestone,
  setSelectedMilestone,
  exceptionName,
  setExceptionName,
  description,
  setDescription,
  exceptionType,
  setExceptionType,
  handleSaveException,
  handleBack,
  onExceptionDirtyChange,
}) => {
  const { showDecisionDialogBox } = useDialogBoxContext();
  
  const initialMilestoneRef = React.useRef(selectedMilestone);

  // Helper function to get valid milestone value
  const getValidMilestoneValue = (milestone: string): string => {
    if (!milestone || !milestones || milestones.length === 0) {
      return milestones && milestones.length > 0 ? milestones[0] : "";
    }
    
    // If the milestone is valid, return it
    if (milestones.includes(milestone)) {
      return milestone;
    }
    
    // If not valid, return the first available milestone
    return milestones[0] || "";
  };

  // Ensure selectedMilestone is always valid
  const validSelectedMilestone = getValidMilestoneValue(selectedMilestone);
  
  // Update the parent if the current selected milestone is invalid
  React.useEffect(() => {
    if (selectedMilestone !== validSelectedMilestone) {
      setSelectedMilestone(validSelectedMilestone);
    }
  }, [selectedMilestone, validSelectedMilestone, setSelectedMilestone]);

  const isSaveDisabled =
    exceptionType === null ||
    !exceptionName.trim() ||
    !milestones.includes(validSelectedMilestone) ||
    !description.trim();

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const text = event.target.value;
    if (text.length <= 280) {
      setDescription(text);
    }
  };

  useEffect(() => {
    const dirty =
      !!exceptionName.trim() &&
      !!description.trim() &&
      validSelectedMilestone !== "All Milestones" &&
      exceptionType != null;

    onExceptionDirtyChange?.(dirty);
  }, [
    exceptionName,
    description,
    validSelectedMilestone,
    exceptionType,
    onExceptionDirtyChange,
  ]);

  const isTryingToLeave =
    !!description.trim() &&
    !!validSelectedMilestone &&
    !!exceptionType &&
    !!exceptionName;
  useBrowserClosureWarning(isTryingToLeave);
  const handleBackButtonCheck = () => {
    if (isTryingToLeave) {
      showDecisionDialogBox(
        true,
        decisionDialogBoxTitle,
        decisionDialogBoxMessage,
        () => {
          handleSaveException(
            exceptionName,
            description,
            validSelectedMilestone,
            exceptionType?.valueOf() as exceptionTypes
          );
          handleBack();
        },
        () => {
          setExceptionName("");
          setDescription("");
          // Revert to the original milestone the user had selected before starting Add Exception
          setSelectedMilestone(initialMilestoneRef.current);
          setExceptionType(null);
          onExceptionDirtyChange?.(false);
          handleBack();
        }
      );
    } else {
      setSelectedMilestone(initialMilestoneRef.current);
      handleBack();
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <IconButton onClick={handleBackButtonCheck}>
          <ArrowBackIcon sx={{ color: "#1C2B36" }} />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "16px" }}>
          Add Exception
        </Typography>
      </Box>

      {/* Type Dropdown */}
      <InputLabel
        variant="standard"
        id="type-select-label"
        sx={{
          fontWeight: "500",
          color: "#3E5463",
          fontSize: "16px",
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
        required
      >
        Type
      </InputLabel>
      <FormControl
        fullWidth
        variant="standard"
        style={{ marginBottom: "16px" }}
      >
        <Select
          labelId="type-select-label"
          IconComponent={(props) => (
            <GridExpandMoreIcon
              {...props}
              sx={{
                fontSize: 25,
              }}
            />
          )}
          value={exceptionType || ""}
          sx={{
            fontWeight: "400",
            "& .MuiSelect-icon": {
              color: "#3E5463",
            },
          }}
          onChange={(event) =>
            setExceptionType(event.target.value as exceptionTypes)
          }
        >
          {Object.values(exceptionTypes).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Name Input */}
      <InputLabel
        id="milestone-select-label"
        sx={{
          fontWeight: "500",
          color: "#3E5463",
          fontSize: "16px",
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
        required
      >
        Name
      </InputLabel>
      <TextField
        placeholder="Exception name"
        value={exceptionName}
        onChange={(event) => setExceptionName(event.target.value)}
        fullWidth
        variant="standard"
        style={{ marginBottom: "16px" }}
      />

      {/* Milestone Dropdown */}
      <InputLabel
        id="milestone-select-label"
        sx={{
          fontWeight: "500",
          color: "#3E5463",
          fontSize: "16px",
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
        required
      >
        Select Milestone
      </InputLabel>
      <FormControl
        fullWidth
        variant="standard"
        style={{ marginBottom: "16px" }}
      >
        <Select
          IconComponent={(props) => (
            <GridExpandMoreIcon
              {...props}
              sx={{
                fontSize: 25,
              }}
            />
          )}
          labelId="milestone-select-label"
          value={validSelectedMilestone}
          onChange={(event) => setSelectedMilestone(event.target.value)}
          sx={{
            "& .MuiSelect-icon": {
              color: "#3E5463",
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: STANDARD_HEIGHT_OF_DROPDOWN,
                overflowY: "auto",
                ...thinDarkScrollbar,
              },
            },
          }}
        >
          {milestones.map((milestone) => (
            <MenuItem key={milestone} value={milestone}>
              {milestone}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <InputLabel
        id="milestone-select-label"
        sx={{
          fontWeight: "500",
          color: "#3E5463",
          fontSize: "16px",
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
        required
      >
        Description
      </InputLabel>
      <TextField
        placeholder="Type exception description"
        multiline
        rows={4}
        value={description}
        onChange={handleDescriptionChange}
        fullWidth
        variant="standard"
        inputProps={{ maxLength: 280, style: { color: "#1C2B36" } }}
        style={{ marginBottom: "16px", fontFamily: "Karla, sans-serif" }}
      />
      <Typography
        variant="caption"
        style={{ display: "block", marginBottom: "16px" }}
      >
        {description.length}/280 characters
      </Typography>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          onClick={() => {
            setSelectedMilestone(initialMilestoneRef.current);
            setExceptionName("");
            setDescription("");
            setExceptionType(null);
            onExceptionDirtyChange?.(false);
            handleBack();
          }}
          style={{ textTransform: "none", border: "2px solid #005DAA" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaveDisabled}
          style={{ textTransform: "none" }}
          onClick={() => {
            handleSaveException(
              exceptionName,
              description,
              validSelectedMilestone,
              exceptionType?.valueOf() as exceptionTypes
            );
            onExceptionDirtyChange?.(false);
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default AddExceptionForm;